import {
  FeatureFlag,
  FeatureFlags,
  FeatureFlagValue,
  ConfigFlags,
} from "../types.ts";
import { Experiences } from "../experiencesSchema.ts";

export function buildFeatureFlags<FlagKey extends string>({
  defaultFlagValues,
  configFlags,
  parameterFlags,
  experiences,
  parameterPrefix,
  cookies,
}: {
  defaultFlagValues: Record<FlagKey, FeatureFlagValue>;
  configFlags?: ConfigFlags;
  parameterFlags?: Record<string, unknown>;
  experiences?: Experiences;
  parameterPrefix?: string;
  cookies?: Partial<Record<string, string>>;
}) {
  const defaultFlags = buildDefaultFlags(defaultFlagValues);
  return applyFeatureFlagOverrides({
    defaultFlags,
    experiences,
    parameterPrefix,
    cookies,
    parameterFlags,
    configFlags,
  });
}

function buildDefaultFlags<FlagKey extends string>(
  defaultFlagValues: Record<FlagKey, FeatureFlagValue>,
): FeatureFlags<FlagKey> {
  const defaultFlags = {} as FeatureFlags<FlagKey>;

  for (const key in defaultFlagValues) {
    defaultFlags[key] = {
      source: "default",
      value: defaultFlagValues[key]!,
    };
  }

  return defaultFlags;
}
function applyFeatureFlagOverrides<FlagKey extends string>({
  defaultFlags,
  experiences,
  parameterPrefix,
  cookies,
  parameterFlags,
  configFlags,
}: {
  defaultFlags: FeatureFlags<FlagKey>;
  experiences?: Experiences;
  parameterPrefix?: string;
  cookies?: Partial<Record<string, string>>;
  parameterFlags?: Record<string, unknown>;
  configFlags?: ConfigFlags;
}): FeatureFlags<FlagKey> {
  const experienceOverrides = getExperienceOverrides(
    experiences,
    parameterPrefix,
  );

  const cookieOverrides = getCookieOverrides(
    cookies?.["FEATURE_FLAG_OVERRIDES"],
  );

  const parameterOverrides = parameterFlags || {};

  const configOverrides = configFlags || {};

  const flags = {} as FeatureFlags<FlagKey>;

  for (const key in defaultFlags) {
    switch (true) {
      case key in cookieOverrides:
        flags[key] = cookieOverrides[key]!;
        break;
      case key in experienceOverrides:
        flags[key] = experienceOverrides[key]!;
        break;
      case key in parameterOverrides:
        flags[key] = {
          source: "parameter",
          value: getParameterValue(parameterOverrides[key]),
        };
        break;
      case key in configOverrides:
        flags[key] = {
          source: "config",
          value: getParameterValue(configOverrides[key]),
        };
        break;
      default:
        flags[key] = defaultFlags[key];
        break;
    }
  }

  return flags;
}

export function applyPageConfigOverrides<FlagKey extends string>(
  flags: FeatureFlags<FlagKey>,
  configFlags?: ConfigFlags,
) {
  if (!configFlags) return flags;

  const result = {} as FeatureFlags<FlagKey>;

  for (const key in flags) {
    if (flags[key].source === "default" && key in configFlags) {
      result[key] = {
        source: "config",
        value: configFlags[key]!,
      };
    } else {
      result[key] = flags[key];
    }
  }

  return result;
}

function getExperienceOverrides(
  experiences?: Experiences,
  parameterPrefix: string = "",
) {
  if (!experiences || experiences.length === 0) return {};

  return experiences.reduce(
    (
      acc: Record<string, Extract<FeatureFlag, { source: "monetate" }>>,
      experience,
    ) => {
      Object.entries(experience.parameters || {})
        .filter(([parameterName]) => parameterName.startsWith(parameterPrefix))
        .forEach(([parameterName, parameterValue]) => {
          const flagName = parameterName.replace(parameterPrefix, "");
          if (flagName in acc) {
            // TODO
            // recordExperienceParameterNameCollision();
            // eslint-disable-next-line no-console
            console.error(
              `Duplicate monetate experience parameter found. Parameter name: "${parameterName}". ExperienceIds: ${experience.experienceId}, ${acc[flagName]?.experienceId}`,
            );
          } else {
            acc = {
              ...acc,
              [flagName]: {
                source: "monetate",
                value: getParameterValue(parameterValue),
                experienceName: experience.experienceName,
                experienceId: Number(experience.experienceId),
                impressionId: experience.impressionId ?? null,
                variantLabel: experience.variantLabel,
              },
            };
          }
        });
      return acc;
    },
    {},
  );
}

function getCookieOverrides(featureFlagOverridesCookie?: string) {
  if (!featureFlagOverridesCookie) return {};

  try {
    const parsed: unknown = JSON.parse(featureFlagOverridesCookie);
    const result = {} as Record<
      string,
      Extract<FeatureFlag, { source: "cookie" }>
    >;

    if (parsed && typeof parsed === "object") {
      for (const [key, value] of Object.entries(parsed)) {
        result[key] = {
          source: "cookie",
          value: getParameterValue(value),
        };
      }
    }

    return result;
  } catch (e) {
    return {};
  }
}

const getParameterValue = (value: unknown): FeatureFlagValue => {
  if (["on", "true"].includes(String(value))) {
    return true;
  }
  if (["off", "false"].includes(String(value))) {
    return false;
  }
  return String(value);
};
